/**
 * FoodieMatch Component — Find dining partners (refined)
 */
import React, { useState, useEffect } from 'react';
import { Users, Clock, Plus, Check } from 'lucide-react';
import { Modal, Spinner, ErrorMessage } from '../../shared';
import { matchApi } from '../../../services/api';

export function FoodieMatch({ isOpen, onClose, restaurant, t }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newSession, setNewSession] = useState({
    scheduledTime: '',
    displayName: ''
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;

    async function fetchSessions() {
      setLoading(true);
      try {
        const data = await matchApi.getSessions(restaurant?.id);
        if (mounted) setSessions(data.sessions || []);
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchSessions();
    const interval = setInterval(fetchSessions, 10000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [isOpen, restaurant?.id]);

  const handleJoin = async (sessionId) => {
    setJoining(sessionId);
    setError(null);

    try {
      await matchApi.joinSession(sessionId, newSession.displayName || 'Foodie');
      const data = await matchApi.getSessions(restaurant?.id);
      setSessions(data.sessions || []);
    } catch (err) {
      setError(err);
    } finally {
      setJoining(null);
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    setError(null);

    try {
      await matchApi.createSession({
        restaurantId: restaurant?.id,
        scheduledTime: newSession.scheduledTime || null,
        displayName: newSession.displayName || 'Foodie'
      });

      const data = await matchApi.getSessions(restaurant?.id);
      setSessions(data.sessions || []);
      setShowCreate(false);
      setNewSession({ scheduledTime: '', displayName: '' });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const getAvatarColor = (index) => {
    const colors = ['bg-pink-400', 'bg-violet-400', 'bg-blue-400', 'bg-emerald-400', 'bg-amber-400'];
    return colors[index % colors.length];
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t ? t('findPartners') : 'Find Dining Partners'}
      icon="🤝"
    >
      <div className="space-y-4">
        {loading && sessions.length === 0 ? (
          <div className="flex justify-center py-4">
            <Spinner className="text-pink-500" />
          </div>
        ) : (
          <>
            {restaurant && (
              <p className="text-sm text-stone-500 text-center font-medium">
                Find partners for <strong className="text-stone-700">{restaurant.name}</strong>
              </p>
            )}

            {sessions.length > 0 ? (
              <div className="space-y-2.5">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="p-3 bg-warm-50 rounded-xl border border-stone-100"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Users size={14} className="text-stone-400" />
                        <span className="text-sm font-semibold text-stone-700">
                          {session.participant_count || 0} / {session.max_participants || 10}
                        </span>
                      </div>
                      {session.scheduled_time && (
                        <div className="flex items-center gap-1 text-[11px] text-stone-400 font-medium">
                          <Clock size={11} />
                          {session.scheduled_time}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: Math.min(session.participant_count || 3, 5) }).map((_, i) => (
                        <div
                          key={i}
                          className={`w-7 h-7 rounded-full ${getAvatarColor(i)} flex items-center justify-center text-white text-[10px] font-bold border-2 border-white`}
                        >
                          {String.fromCharCode(65 + i)}
                        </div>
                      ))}
                      {(session.participant_count || 0) > 5 && (
                        <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center text-[10px] text-stone-500 border-2 border-white font-bold">
                          +{(session.participant_count || 0) - 5}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => handleJoin(session.id)}
                      disabled={joining === session.id}
                      className="w-full py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.97]"
                    >
                      {joining === session.id ? (
                        <Spinner size="sm" className="text-white" />
                      ) : (
                        <>
                          <Check size={14} />
                          {t ? t('joinGroup') : 'Join Group'}
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-stone-500 font-medium">No active sessions</p>
                <p className="text-xs text-stone-400 mt-1">Be the first to create one!</p>
              </div>
            )}

            {showCreate ? (
              <div className="p-3.5 bg-pink-50/60 rounded-xl border border-pink-100 space-y-2.5">
                <input
                  type="text"
                  placeholder="Your display name"
                  value={newSession.displayName}
                  onChange={(e) => setNewSession({ ...newSession, displayName: e.target.value })}
                  className="w-full bg-white rounded-xl px-3.5 py-2.5 text-sm border border-pink-200/60 focus:outline-none focus:ring-2 focus:ring-pink-200 text-stone-700 placeholder:text-stone-400 transition-all duration-200"
                />
                <input
                  type="time"
                  value={newSession.scheduledTime}
                  onChange={(e) => setNewSession({ ...newSession, scheduledTime: e.target.value })}
                  className="w-full bg-white rounded-xl px-3.5 py-2.5 text-sm border border-pink-200/60 focus:outline-none focus:ring-2 focus:ring-pink-200 text-stone-700 transition-all duration-200"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleCreate}
                    disabled={loading}
                    className="flex-1 py-2.5 bg-pink-500 hover:bg-pink-600 text-white rounded-xl text-sm font-semibold disabled:opacity-50 transition-all duration-200"
                  >
                    {loading ? <Spinner size="sm" className="mx-auto text-white" /> : 'Create'}
                  </button>
                  <button
                    onClick={() => setShowCreate(false)}
                    className="px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-xl text-sm font-medium transition-all duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowCreate(true)}
                className="w-full py-2.5 border-2 border-dashed border-pink-200 text-pink-500 rounded-xl text-sm font-semibold hover:bg-pink-50/50 flex items-center justify-center gap-2 transition-all duration-200"
              >
                <Plus size={14} />
                Create New Session
              </button>
            )}
          </>
        )}

        {error && <ErrorMessage error={error} />}

        <button
          onClick={onClose}
          className="w-full text-sm text-stone-400 hover:text-stone-600 font-medium py-2 transition-colors"
        >
          Maybe later
        </button>
      </div>
    </Modal>
  );
}

export default FoodieMatch;
